<template>
  <div class="global-quill-editor">
    <div v-if="isUserAuthorized && loaded">
      <div v-if="autosave" class="snippet-autosave-header">
        <div class="snippet-autosave-status">
          <strong>Status:</strong>
          <span
            class="status-indicator"
            :class="{
              'text-orange': status === 'editing',
              'text-red': status === 'saving',
              'text-green': status === 'ready',
              'text-blue': isReadOnly,
            }"
          >
            {{
              isReadOnly
                ? 'Read-only (Translated content)'
                : status === 'editing'
                ? 'Editing...'
                : status === 'saving'
                ? 'Saving...'
                : 'Ready!'
            }}
          </span>
        </div>
        <div v-if="isReadOnly" class="read-only-notice">
          <em
            >⚠️ This is translated content. Edit the English version to make
            changes.</em
          >
        </div>
      </div>

      <CustomQuill
        v-model="content"
        :readonly="isReadOnly"
        @text-change="onTextChange"
        @selection-change="onSelectionChange"
      />
    </div>

    <span v-else v-html="content"></span>
  </div>
</template>

<script setup>
  import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
  import { onBeforeRouteLeave, useRoute } from 'vue-router'
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import CustomQuill from '@/components/utilities/CustomQuill.vue'

  const props = defineProps({
    page: String,
    firestoreCollection: { type: String, required: true },
    allowedRoles: { type: Array, default: () => ['superAdmin'] },
    docId: String,
    initialData: Object,
    autosave: { type: Boolean, default: true },
  })

  // const emit = defineEmits(['update-content'])
  const emit = defineEmits(['update-content', 'update:hasContent'])

  const roles = ref([])
  const content = ref('')
  const contentId = ref(props.docId || null)
  const timeout = ref(null)
  const loaded = ref(false)
  const status = ref('ready')
  const hasEditedOnce = ref(false)

  const isUserAuthorized = computed(() => {
    const actualRoles = Array.isArray(roles.value)
      ? roles.value
      : roles.value?.value
    return actualRoles?.some((r) => props.allowedRoles.includes(r))
  })

  // Determine if editor should be read-only (when viewing translated content)
  const isReadOnly = computed(() => {
    // Extract locale from URL, default to 'en' if not a valid locale
    const pathParts = window.location.pathname.split('/').filter((part) => part)
    const currentLocale =
      pathParts.length > 0 && ['en', 'pt'].includes(pathParts[0])
        ? pathParts[0]
        : 'en'
    return currentLocale !== 'en' && props.firestoreCollection === 'howtos'
  })

  // Function to load content based on current locale
  const loadContent = () => {
    if (!props.initialData) {
      // Extract page name from URL, removing locale prefix if present
      let pagePath = props.page
      if (!pagePath) {
        const pathParts = window.location.pathname
          .split('/')
          .filter((part) => part)
        // Remove locale if present (first part if it's 'en', 'pt', etc.)
        if (pathParts.length > 0 && ['en', 'pt'].includes(pathParts[0])) {
          pathParts.shift() // Remove locale
        }
        // Get the last part as the page name (e.g., 'notifications' from 'admin/howtos/notifications')
        pagePath = pathParts[pathParts.length - 1] || 'notifications'
      }

      // Extract locale from URL, default to 'en' if not a valid locale
      const pathParts = window.location.pathname
        .split('/')
        .filter((part) => part)

      const currentLocale =
        pathParts.length > 0 && ['en', 'pt'].includes(pathParts[0])
          ? pathParts[0]
          : 'en'

      db.collection(props.firestoreCollection)
        .get()
        .then((snapshot) => {
          let matchedDoc = null
          snapshot.forEach((doc) => {
            const data = doc.data()
            if (data.page === pagePath) matchedDoc = { ...data, id: doc.id }
          })
          if (!matchedDoc && snapshot.size > 0) {
            // For howtos collection, don't use fallback - create new document instead
            if (props.firestoreCollection === 'howtos') {
              console.log(
                `⚠️ No howto document found for page "${pagePath}" - will create new document when content is saved`
              )
              matchedDoc = null
            } else {
              const fallback = snapshot.docs[0]
              matchedDoc = { ...fallback.data(), id: fallback.id }
            }
          }
          if (matchedDoc) {
            console.log(`🌍 Loading content for locale: ${currentLocale}`)
            console.log(
              `📄 Available variants: ${Object.keys(
                matchedDoc.variants?.default || {}
              )}`
            )

            // Load priority:
            // - English/source: `content` (canonical; saves write here)
            // - Other locales: variants.default.{locale} → content fallback
            let docContent = ''
            if (currentLocale === 'en' && matchedDoc.content) {
              docContent = matchedDoc.content
              console.log(
                `📝 Loaded English source content (${docContent.length} chars)`
              )
            } else if (matchedDoc.variants?.default?.[currentLocale]) {
              docContent = matchedDoc.variants.default[currentLocale]
              console.log(
                `✅ Loaded translated content for ${currentLocale} (${docContent.length} chars)`
              )
            } else if (matchedDoc.content) {
              docContent = matchedDoc.content
              console.log(
                `📝 Loaded English source content (${docContent.length} chars)`
              )
            } else {
              console.log(`⚠️ No content found`)
            }

            content.value = docContent
            contentId.value = matchedDoc.id
            loaded.value = true
            emit('update:hasContent', !!docContent?.trim())
          } else {
            loaded.value = true
            emit('update:hasContent', false)
          }
        })
    } else {
      // Use initial data if provided
      content.value =
        props.firestoreCollection === 'messages'
          ? props.initialData.message ?? ''
          : props.initialData.content ?? ''
      loaded.value = true
    }
  }

  function save(data) {
    // check if user is authenticated
    const user = firebase.auth().currentUser
    if (!user) {
      console.warn('⚠️ Skipping save: user is not authenticated')
      return
    }

    // Prevent saving when in read-only mode (viewing translated content)
    if (isReadOnly.value) {
      console.warn(
        '⚠️ Skipping save: editor is in read-only mode for translated content'
      )
      return
    }

    status.value = 'saving'
    if (timeout.value) clearTimeout(timeout.value)

    timeout.value = setTimeout(() => {
      // Extract page name from URL, removing locale prefix if present (same logic as loadContent)
      let pageValue = props.page
      if (!pageValue) {
        const pathParts = window.location.pathname
          .split('/')
          .filter((part) => part)
        // Remove locale if present (first part if it's 'en', 'pt', etc.)
        if (pathParts.length > 0 && ['en', 'pt'].includes(pathParts[0])) {
          pathParts.shift() // Remove locale
        }
        // Get the last part as the page name (e.g., 'notifications' from 'admin/howtos/notifications')
        pageValue = pathParts[pathParts.length - 1] || 'notifications'
      }

      let updateData = {}

      if (props.initialData?.message !== undefined) {
        // Messages collection
        updateData = {
          message: typeof data === 'string' ? data : '',
          page: pageValue,
        }
      } else if (props.firestoreCollection === 'howtos') {
        // Howtos collection - save to content field (English source)
        // Don't update the page field - it should never change after creation
        updateData = {
          content: typeof data === 'string' ? data : '',
          autoTranslate: true,
          sourceLocale: 'en',
        }
      } else if (props.firestoreCollection === 'appguide') {
        // content is the English source of truth; CF syncs variants.default.en + translations
        updateData = {
          content: typeof data === 'string' ? data : '',
          page: pageValue,
          autoTranslate: true,
          sourceLocale: 'en',
        }
      } else {
        // Other collections (privacy_policy, etc.)
        updateData = {
          content: typeof data === 'string' ? data : '',
          page: pageValue,
        }
      }

      // Handle case where no document was found (for howtos collection)
      if (!contentId.value && props.firestoreCollection === 'howtos') {
        // Create new document for this page
        db.collection(props.firestoreCollection)
          .add(updateData)
          .then((docRef) => {
            contentId.value = docRef.id
            status.value = 'ready'
            console.log(
              `✅ Created new howto document: ${docRef.id} for page: ${pageValue}`
            )
          })
          .catch((err) => {
            console.error('❌ Save error:', err)
            status.value = 'error'
          })
      } else if (contentId.value) {
        // Update existing document
        db.collection(props.firestoreCollection)
          .doc(contentId.value)
          .set(updateData, { merge: true })
          .then(() => {
            status.value = 'ready'
          })
          .catch((err) => {
            console.error('❌ Save error:', err)
            status.value = 'error'
          })
      } else {
        console.error('❌ No document ID available for saving')
        status.value = 'error'
      }
    }, 500)
  }

  function onTextChange() {
    if (!props.autosave) return // ⛔ STOP if autosave is disabled
    if (isReadOnly.value) return // ⛔ STOP if in read-only mode

    if (!loaded.value || !hasEditedOnce.value) {
      hasEditedOnce.value = true
      return
    }

    // Check if we're in the middle of a locale change
    const currentLocale = window.location.pathname
      .split('/')
      .filter((part) => part)
    const detectedLocale =
      currentLocale.length > 0 && ['en', 'pt'].includes(currentLocale[0])
        ? currentLocale[0]
        : 'en'

    if (detectedLocale !== 'en') {
      console.log(
        `⛔ Skipping save during locale change - detected locale: ${detectedLocale}`
      )
      return
    }

    status.value = 'editing'

    if (timeout.value) clearTimeout(timeout.value)

    timeout.value = setTimeout(() => {
      save(content.value)
    }, 1000)
  }

  function onSelectionChange() {
    // Optional hook
  }

  let stopRolesWatch = null
  let stopAuthWatch = null
  onBeforeUnmount(() => {
    if (stopAuthWatch) stopAuthWatch()
    if (stopRolesWatch) stopRolesWatch()
  })

  onMounted(() => {
    // Firebase auth listener to clear save timer on logout
    stopAuthWatch = firebase.auth().onAuthStateChanged((user) => {
      if (!user) {
        if (stopRolesWatch) stopRolesWatch()
        stopRolesWatch = null
        // 🛑 Cancel pending save on logout
        if (timeout.value) {
          clearTimeout(timeout.value)
          timeout.value = null
          status.value = 'ready'
          console.warn('🛑 Save cancelled — user logged out')
        }
        return
      } else {
        // ✅ Fetch user roles
        if (stopRolesWatch) stopRolesWatch()
        stopRolesWatch = db.collection('users')
          .where('email', '==', user.email)
          .onSnapshot(
            (snapshot) => {
              snapshot.forEach((doc) => {
                roles.value = doc.data().roles || []
              })
            },
            // Denied once the user signs out (firestore.rules); nothing to do.
            () => { stopRolesWatch = null }
          )
      }
    })

    // Load initial content
    loadContent()
  })

  watch(content, (newVal) => {
    emit('update-content', newVal)
    emit('update:hasContent', !!newVal?.trim())
  })

  // Watch for route changes to reload content with correct locale
  const route = useRoute()
  watch(
    () => route.path,
    (newPath, oldPath) => {
      if (newPath !== oldPath && loaded.value) {
        console.log(
          `🔄 Route changed from ${oldPath} to ${newPath}, reloading content...`
        )
        console.log(
          `🔄 Current URL during route change: ${window.location.pathname}`
        )
        // Reload content with new locale
        loadContent()
      }
    },
    { immediate: false }
  )

  onBeforeRouteLeave(() => {
    if (!props.autosave) return // ⛔ STOP if autosave is disabled

    const user = firebase.auth().currentUser
    if (!user) return

    if (timeout.value) clearTimeout(timeout.value)
    save(content.value)
  })
</script>

<style scoped>
  .global-quill-editor {
    margin-bottom: 10px;
  }
  .global-quill-editor img {
    max-width: 100%;
  }
  .global-quill-editor .ql-editor {
    min-height: 180px;
    max-height: 400px;
    overflow-y: auto;
  }
  :deep(.ql-editor) {
    font-size: 16px;
  }
  .status-indicator {
    margin-left: 7px;
  }
  .read-only-notice {
    margin-top: 5px;
    padding: 5px;
    background-color: #f0f8ff;
    border: 1px solid #b3d9ff;
    border-radius: 3px;
    font-size: 12px;
  }
  .text-blue {
    color: #1976d2;
  }
</style>
